/**
 * <galaxy-category-slider>: fades through its [data-slide] children.
 * Autoplays only while on screen, not hovered or focused, and not for reduced-motion visitors.
 * Buttons with [data-go] jump to a slide, [data-step] (-1 / 1) go back or forward, and a horizontal
 * swipe or mouse drag does the same; the active bar fills over the interval (CSS).
 * Only the first slide's photo comes with the page: the next one loads once the slider is near the
 * screen and each change loads the one after; the previous one loads as soon as someone interacts.
 */
class GalaxyCategorySlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.bars = [...this.querySelectorAll('[data-go]')];
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 7000;
    this.style.setProperty('--galaxy-cslider-interval', `${this.interval}ms`);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.visible = false;
    this.held = false;

    this.bars.forEach((bar) =>
      bar.addEventListener('click', () => {
        this.show(Number(bar.dataset.go));
        this.restart();
      })
    );
    this.querySelectorAll('[data-step]').forEach((button) =>
      button.addEventListener('click', () => {
        this.show(this.index + Number(/** @type {HTMLElement} */ (button).dataset.step));
        this.restart();
      })
    );
    if (this.slides.length > 1) this.#bindSwipe();

    // Load the next photo ahead of time, and the previous one when a visitor starts interacting.
    this.loadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.#load(this.index + 1);
        this.loadObserver?.disconnect();
      },
      { rootMargin: '400px 0px' }
    );
    this.loadObserver.observe(this);
    const loadPrevious = () => this.#load(this.index - 1);
    this.addEventListener('pointerenter', loadPrevious);
    this.addEventListener('pointerdown', loadPrevious);
    this.addEventListener('focusin', loadPrevious);

    if (this.slides.length < 2 || this.reduced || window.Shopify?.designMode) return;

    const hold = () => {
      this.held = true;
      this.update();
    };
    const release = () => {
      this.held = false;
      this.update();
    };
    this.addEventListener('pointerenter', hold);
    this.addEventListener('pointerleave', release);
    // Keyboard focus pauses; focus left behind by a click or tap does not (it would keep this slider paused).
    this.addEventListener('focusin', (event) => {
      if (/** @type {Element} */ (event.target).matches(':focus-visible')) hold();
    });
    this.addEventListener('focusout', release);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting;
        this.update();
      },
      { threshold: 0.25 }
    );
    this.observer.observe(this);
  }

  /* A horizontal swipe or drag of more than 40px moves one slide; vertical scrolling is left alone. */
  #bindSwipe() {
    /** @type {{ x: number, y: number, id: number } | null} */
    let start = null;
    this.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || /** @type {Element} */ (event.target).closest('a, button')) return;
      start = { x: event.clientX, y: event.clientY, id: event.pointerId };
    });
    this.addEventListener('pointerup', (event) => {
      if (!start || event.pointerId !== start.id) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      start = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.show(this.index + (dx < 0 ? 1 : -1));
      this.restart();
    });
    this.addEventListener('pointercancel', () => (start = null));
    this.addEventListener('dragstart', (event) => event.preventDefault());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.loadObserver?.disconnect();
    clearTimeout(this.timer);
  }

  /** Give a slide's photo its real addresses (kept in data-srcset / data-src until needed). @param {number} i */
  #load(i) {
    const slide = this.slides[(i + this.slides.length) % this.slides.length];
    slide?.querySelectorAll('[data-srcset], [data-src]').forEach((el) => {
      if (el.dataset.srcset) el.setAttribute('srcset', el.dataset.srcset);
      if (el.dataset.src) el.setAttribute('src', el.dataset.src);
      el.removeAttribute('data-srcset');
      el.removeAttribute('data-src');
    });
  }

  show(next) {
    const count = this.slides.length;
    this.index = (next + count) % count;
    this.#load(this.index);
    this.#load(this.index + 1);
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('aria-hidden', !active);
      slide.querySelectorAll('a').forEach((link) => (active ? link.removeAttribute('tabindex') : link.setAttribute('tabindex', '-1')));
    });
    this.bars.forEach((bar, i) => (i === this.index ? bar.setAttribute('aria-current', 'true') : bar.removeAttribute('aria-current')));
  }

  /* Restart the fill animation of the active bar; while paused (hover / focus) it waits for the release. */
  restart() {
    if (!this.hasAttribute('data-playing')) return;
    this.removeAttribute('data-playing');
    void this.offsetWidth;
    this.setAttribute('data-playing', '');
    if (this.hasAttribute('data-paused')) {
      this.remaining = this.interval;
      return;
    }
    this.schedule(this.interval);
  }

  update() {
    const run = this.visible && !this.held;
    if (run && !this.hasAttribute('data-playing')) {
      this.setAttribute('data-playing', '');
      this.removeAttribute('data-paused');
      this.started = Date.now();
      this.remaining = this.interval;
      this.schedule(this.remaining);
    } else if (run && this.hasAttribute('data-paused')) {
      this.removeAttribute('data-paused');
      this.started = Date.now();
      this.schedule(this.remaining);
    } else if (!run && this.hasAttribute('data-playing') && !this.hasAttribute('data-paused')) {
      this.setAttribute('data-paused', '');
      clearTimeout(this.timer);
      this.remaining = Math.max(0, this.remaining - (Date.now() - this.started));
    }
  }

  schedule(delay) {
    clearTimeout(this.timer);
    this.started = Date.now();
    this.remaining = delay;
    this.timer = setTimeout(() => {
      this.show(this.index + 1);
      this.removeAttribute('data-playing');
      void this.offsetWidth;
      this.setAttribute('data-playing', '');
      this.schedule(this.interval);
    }, delay);
  }
}

if (!customElements.get('galaxy-category-slider')) {
  customElements.define('galaxy-category-slider', GalaxyCategorySlider);
}
